// A Stats tile showing a team (?live=1; teams and matches, Ben 2026-10-30 — docs/modules/
// teams-and-matches.md "3. Stats tiles"). The same tile as a player's: drop a team on it (or pick one
// in its search). The same two rows as a player's too (rules.md "Two rows, two jobs"; Ben,
// 2026-11-01: on a team "we lose the year selection drop down. We also lose any ability to filter
// the range"): a season dropdown moves the team's dot; the Season and League chips pick who it's
// compared with (by default the other teams in its league that season, rules.md Row 2;
// live/team-stats-group.ts). The band is that group, the dot the team; hovering it gives its
// percentile among them. A For / Against switch shows the team's own numbers or its opponents'
// (shots taken / shots conceded). Per game.
// The team-only tiles (Possession, Set Pieces, Discipline) have no search box in their header, so
// a team picker under it names the team and switches to another in the league (Ben, 2026-11-01:
// "I have no idea who I am looking at").

import { useEffect } from "react";
import { getModState, setModState } from "./bridge.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { SubControls } from "./SubControls.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect } from "./StyledSelect.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { leagueOptionInfo } from "./CountryFlag.js";
import { applyTeamDrop } from "../canvas/team-modules.js";
import { canvasApi } from "../canvas/shell-actions.js";
import { undoApi } from "../canvas/undo.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainTeamStats } from "./explain.js";
import { markerFraction, percentBetter, percentBetterText } from "./stats-view.js";
import {
  formatMetricValue,
  selectedValuesOf,
  type ActiveFilters,
  type CategoricalActive,
  type ExcludeValue,
} from "../engines/stats-filter-kit.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQueries } from "../live/query.js";
import { leagueSeasonsQuery, teamLowerIsBetter, teamStatsQuery } from "../live/team-stats.js";
import { teamGroupBar, teamGroupLabel, teamGroupNoun, teamGroupPicks } from "../live/team-stats-group.js";
import { seasonChipFromValue, seasonChipValue, seasonLabel, type SeasonChip } from "../live/stats-files.js";
import type { TeamRef } from "../live/team.js";

type Side = "for" | "against";
const SIDE_LABELS: Record<string, string> = { for: "For", against: "Against" };
const CHIP_KEYS = ["season", "league"] as const;
const CHIP_LABELS = { season: "Season", league: "League" };
// Both chips are the defaults (rules.md "Default chips are few and quiet"): quiet until touched.
const QUIET_BOTH: ReadonlySet<string> = new Set(["season", "league"]);
const QUIET_SEASON: ReadonlySet<string> = new Set(["season"]);
const QUIET_LEAGUE: ReadonlySet<string> = new Set(["league"]);
// The chips take no player values; the bar only asks for one on the snapshot site.
const NO_PLAYER = {};

export function TeamStats({ mod, body, team, categoryId, onChange }: { mod: HTMLElement; body: HTMLElement; team: TeamRef; categoryId: string; onChange: () => void }) {
  const choices = useLiveLeagueChoices();
  const league = choices.find((c) => c.id === team.competitionId) ?? null;
  // Men with men, women with women (rules.md): the League chip lists the team's side only.
  const sameSide = choices.filter((c) => c.womens === (league?.womens ?? null));
  const [ownSeasons] = useLiveQueries([leagueSeasonsQuery(team.competitionId)]) as [string[]];
  // The season it was picked up in (or picked in the dropdown), else the league's newest.
  const seasonCode = team.season ?? ownSeasons[0] ?? null;

  // Row 2's state: undefined = untouched (the team's season / league, quiet).
  const seasonChip = getModState<SeasonChip>(mod, "_teamStatsSeasonChip");
  const storedLeagues = getModState<number[]>(mod, "_teamStatsLeagues");
  const leagueIds = storedLeagues ?? [team.competitionId];
  const leagueSeasons = useLiveQueries(leagueIds.map(leagueSeasonsQuery));
  const picks = seasonCode ? teamGroupPicks(leagueIds.map((id, i) => ({ id, seasons: leagueSeasons[i]! })), seasonChip, seasonCode) : [];
  // The team's own league season (its dot, the stat list, the picker), then the group's.
  const [own, ...groupAnswers] = useLiveQueries([
    ...(seasonCode ? [teamStatsQuery(team.competitionId, seasonCode, categoryId)] : []),
    ...picks.map((p) => teamStatsQuery(p.competitionId, p.season, categoryId)),
  ]);
  const groupRows = groupAnswers.flatMap((a) => a!.rows);

  const side: Side = getModState<Side>(mod, "_teamStatsSide") === "against" ? "against" : "for";
  const flip = () => {
    setModState(mod, "_teamStatsSide", side === "for" ? "against" : undefined);
    onChange();
  };
  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);

  const leagueName = league?.label ?? "league";
  const season = seasonCode ? humanizeSeasonCode(seasonCode) : "";
  const groups = own ? own[side] : [];
  const ownRow = own?.rows.find((r) => r.teamId === team.id) ?? null;
  const count = groupRows.length;
  const hasAgainst = !!own && own.against.some((g) => g.metrics.length > 0);
  const labelOf = (id: number) => choices.find((c) => c.id === id)?.label ?? String(id);
  const groupLeagues = leagueIds.map(labelOf);
  const groupLabel = seasonCode ? teamGroupLabel(groupLeagues, seasonChip, seasonCode) : `${leagueName} teams`;
  const who = `${groupLeagues.length === 1 ? groupLeagues[0] : `${groupLeagues.length} leagues'`} ${teamGroupNoun(seasonChip)} compared`;

  // Row 1: the season dropdown (the team's numbers). A new season keeps the chips; an untouched
  // Season chip follows it.
  const pickSeason = (code: string) => {
    if (code === seasonCode) return;
    undoApi().saveState();
    setModState(mod, "_team", { ...team, season: code });
    canvasApi().refreshModuleBody(mod);
  };
  const seasonSelect = (
    <StyledSelect
      items={ownSeasons.map((c) => ({ value: c, label: humanizeSeasonCode(c) }))}
      value={seasonCode ?? ""}
      onChange={pickSeason}
      wrapClass="season-select-wrap"
      extraClass="season-select"
    />
  );

  // A tile with a search box names the team there; one without gets the picker.
  const hasSearchBox = mod.querySelector(".player-search") !== null;
  const teamItems = (own?.rows ?? [])
    .map((r) => ({ value: String(r.teamId), label: r.teamName }))
    .sort((a, b) => a.label.localeCompare(b.label));
  // Before the stats load (or a team missing from them), the team alone, so its name still shows.
  if (!teamItems.some((t) => t.value === String(team.id))) teamItems.unshift({ value: String(team.id), label: team.name });
  const pickTeam = (value: string) => {
    const row = own?.rows.find((r) => String(r.teamId) === value);
    if (!row || row.teamId === team.id) return;
    undoApi().saveState();
    applyTeamDrop(mod, { ...team, id: row.teamId, name: row.teamName });
  };

  // Row 2: what the chip bar shows and edits (a fresh object each render; the bar edits it in
  // place, then onChipsChange writes it back).
  const seasonShown = seasonCode ? seasonChipValue(seasonChip, Number(seasonCode)) : null;
  const leagueShown: CategoricalActive | undefined =
    leagueIds.length === 0 ? undefined : groupLeagues.length === 1 ? groupLeagues[0]! : groupLeagues;
  const chipView: ActiveFilters = {};
  if (seasonShown !== null) chipView.season = seasonShown;
  if (leagueShown !== undefined) chipView.league = leagueShown;
  const seasonOptions = ownSeasons.map((c) => ({ id: Number(c), label: seasonLabel(Number(c)) }));
  const onChipsChange = () => {
    if (JSON.stringify(chipView.season) !== JSON.stringify(seasonShown ?? undefined)) {
      const next: SeasonChip =
        chipView.season === undefined ? null : seasonChipFromValue(chipView.season as string | ExcludeValue, seasonOptions);
      setModState(mod, "_teamStatsSeasonChip", next);
    }
    if (JSON.stringify(chipView.league) !== JSON.stringify(leagueShown)) {
      const ids = selectedValuesOf(chipView.league as CategoricalActive | undefined)
        .map((label) => sameSide.find((c) => c.label === label)?.id)
        .filter((id): id is number => id !== undefined);
      setModState(mod, "_teamStatsLeagues", ids);
    }
    onChange();
  };
  const quiet =
    seasonChip === undefined && storedLeagues === undefined
      ? QUIET_BOTH
      : seasonChip === undefined
        ? QUIET_SEASON
        : storedLeagues === undefined
          ? QUIET_LEAGUE
          : undefined;

  const empty =
    leagueIds.length === 0
      ? `Add a league to compare ${team.name} with.`
      : !own || !ownRow
        ? `No team stats for ${team.name} in the ${leagueName} in ${season || "this season"} yet.`
        : count < 2
          ? `Not enough teams to compare with: ${groupLabel}.`
          : null;

  return (
    <>
      {hasSearchBox ? (
        <SubControlsSlot mod={mod}>{seasonSelect}</SubControlsSlot>
      ) : (
        <SubControls mod={mod} body={body}>
          <StyledSelect
            items={teamItems}
            value={String(team.id)}
            onChange={pickTeam}
            wrapClass="squads-team-select-wrap"
            extraClass="squads-team-select"
            searchable={teamItems.length > 12}
          />
          {seasonSelect}
        </SubControls>
      )}
      <PopulationReadout
        mod={mod}
        body={body}
        count={count}
        showToggle={hasAgainst}
        mode={side}
        onToggle={flip}
        label={groupLabel}
        modeLabels={SIDE_LABELS}
      />
      <div className="stats-wrap">
        <ModuleExplainer mod={mod} paragraphs={explainTeamStats({ team: team.name, group: groupLabel, season, count, side, hasAgainst })} />
        <div className="filter-bar">
          <FilterChipBar
            active={chipView}
            refPlayer={NO_PLAYER}
            options={{ season: seasonOptions.map((o) => o.label), league: sameSide.map((c) => c.label) }}
            rangeFilters={{}}
            onChange={onChipsChange}
            addMenuKeys={CHIP_KEYS}
            labels={CHIP_LABELS}
            quietKeys={quiet}
            newChipValue={(key) => (key === "season" && seasonCode ? seasonLabel(Number(seasonCode)) : undefined)}
            optionInfo={{ league: leagueOptionInfo(sameSide) }}
          />
        </div>
        {empty !== null ? (
          <div className="stats-rows">
            <div className="empty-note">{empty}</div>
          </div>
        ) : (
          <div className="stats-rows">
            {groups.map((g) => (
              <div className="stat-group" key={g.id}>
                {groups.length > 1 && <div className="stat-group-label">{g.name}</div>}
                {g.metrics.map((m) => {
                  const bar = teamGroupBar(groupRows, ownRow?.values[m.key], m.key);
                  const valueText = formatMetricValue(bar.value, m.outputType);
                  const lower = teamLowerIsBetter(m.key);
                  const pct = bar.value === null ? null : percentBetter(bar.value, bar.values, lower);
                  const tip = `${season}: ${valueText}${pct === null ? "" : `\n${percentBetterText(pct, bar.values.length, who, lower)}`}`;
                  // The band is the group; the track also reaches the team when it sits outside it.
                  const bandLeft = bar.band && bar.track ? markerFraction(bar.band.min, bar.track) : 0;
                  const bandRight = bar.band && bar.track ? markerFraction(bar.band.max, bar.track) : 1;
                  return (
                    <div className="pbar-row" data-key={m.key} key={m.key}>
                      <span className="pbar-label-wrap">
                        <span className="pbar-label">{m.shortName || m.name}</span>
                        <span className="pbar-info-icon" data-tooltip={m.definition}>
                          ⓘ
                        </span>
                      </span>
                      <span className="pbar-track" style={!bar.track ? { visibility: "hidden" } : undefined}>
                        <span
                          className="pbar-range"
                          style={{ opacity: bar.track ? 1 : 0, left: `${(bandLeft * 100).toFixed(1)}%`, width: `${((bandRight - bandLeft) * 100).toFixed(1)}%` }}
                        />
                        <span
                          className="pbar-marker"
                          style={bar.value === null || !bar.track ? { opacity: 0 } : { opacity: 1, left: `${(markerFraction(bar.value, bar.track) * 100).toFixed(1)}%` }}
                          {...(bar.value === null ? {} : { "data-tooltip": tip })}
                        />
                      </span>
                      <span className="pbar-value">{valueText}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
